import { MyImage } from '~/components/ui/MyImage'
import {
  ActionType,
  AffectedType,
  AiArtworkOptionTypeEnum,
  SocialViewType,
  collectStyleModel,
  collectStyleModelVariables,
  deleteCustomStyle,
  deleteCustomStyleVariables,
} from '~/graphqls/types'
import useStyleModel, { StyleModelOption } from './useStyleModel'
import classNames from 'classnames'
import { MyIcon } from '~/components/ui/MyIcon'
import { useEffect, useMemo, useState } from 'react'
import { useAtom } from 'jotai'
import { OptionsTabs } from '../OptionsTabs'
import { graphQLErrorMessage, uniqueFunc } from '~/utils'
import { MyModal } from '~/components/ui/MyModal'
import { MyBtn } from '~/components/ui/MyBtn'
import { useMutation } from '@apollo/client'
import { COLLECT_STYLE_MODEL, DELETE_CUSTOM_STYLE } from '~/graphqls/gqls'
import { Modal, message } from 'antd'
import { MyEmpty } from '~/components/ui/MyEmpty'
import style from './StyleModelComp.module.css'
import {
  modelCodeAtom,
  optionsAtom,
  paintTimesAtom,
  paintTimesStepAtom,
  resolutionKeyAtom,
  styleModelAtom,
  isMJStyleModelAtom,
} from './atom'

const DEFAULT_SHOW_COUNT = 19

const StyleModelItem: React.FC<{
  checked: boolean
  onClick: () => void
  data: StyleModelOption
  onCollect: () => void
  style?: React.CSSProperties
  imageClassName?: string
}> = ({ checked, onClick, data, onCollect, style, imageClassName }) => {
  const styleModelTag = useMemo(() => {
    if (data?.modelForVip) {
      return (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/vip_model.png"
          alt=""
        />
      )
    }
    if (data?.isNew) {
      return (
        <img
          className="h-[14px] absolute top-1 right-1"
          src="/images/ai/new.png"
          alt=""
        />
      )
    }
    if (data?.type === AiArtworkOptionTypeEnum.CUSTOM_STYLE_PARAM) {
      return (
        <img
          className="h-[16px] absolute top-1 left-1"
          src="/images/ai/private.png"
          alt=""
        />
      )
    }
    return null
  }, [data])
  return (
    <div
      className={classNames(
        'relative bg-neutral-200 rounded-lg cursor-pointer border-2',
        [checked ? 'border-neutral-800' : 'border-transparent']
      )}
      style={style}
      onClick={onClick}
    >
      <MyImage
        tag="v400"
        className={classNames('w-full rounded-[6px] mb-1', imageClassName)}
        src={data.url}
      />
      {styleModelTag}

      <div className="flex justify-between items-center text-xs px-2">
        <span className="text-neutral-800">{data.name}</span>
        <span
          className={data.collected ? 'text-[#FFD600]' : 'text-neutral-400'}
          onClick={e => {
            e.stopPropagation()
            onCollect()
          }}
        >
          <MyIcon
            className="mt-0.5"
            icon={data.collected ? 'star-filled' : 'star'}
          />{' '}
          {/* 暂时先留着 */}
          {/* {data.collectsCount} */}
        </span>
      </div>
    </div>
  )
}

export const StyleModelComp = () => {
  const [visible, setVisible] = useState(false)

  const [options] = useAtom(optionsAtom)
  const [modelCode, setModelCode] = useAtom(modelCodeAtom)
  const [, setResolutionKey] = useAtom(resolutionKeyAtom)
  const [styleModel, setStyleModel] = useAtom(styleModelAtom)
  const [, setPaintTimes] = useAtom(paintTimesAtom)
  const [, setPaintTimesStep] = useAtom(paintTimesStepAtom)
  const [, setIsMJStyleModel] = useAtom(isMJStyleModelAtom)

  const [collectModelAction] = useMutation<
    collectStyleModel,
    collectStyleModelVariables
  >(COLLECT_STYLE_MODEL)

  const [deleteCustomStyleAction] = useMutation<
    deleteCustomStyle,
    deleteCustomStyleVariables
  >(DELETE_CUSTOM_STYLE)

  const {
    styleModelTabs,
    curStyleModelOptions,
    allStyleModelOptions,
    curStyleModelTabIndex,
    setCurStyleModelTabIndex,
    onCollectOption,
  } = useStyleModel(options!)

  /** 选中的如果不在显示的选项里则添加在最后面 */
  const defaultOptions = uniqueFunc(
    [
      ...curStyleModelOptions.slice(0, DEFAULT_SHOW_COUNT),
      ...curStyleModelOptions.filter(o => o.key === styleModel),
    ],
    'key'
  )

  const onItemClick = (item: StyleModelOption) => {
    setStyleModel(item.key!)
    setModelCode(item.modelCode!)
    setResolutionKey(item.resolutionKey!)
  }

  const onCollect = (item: StyleModelOption) => {
    if (item.type === AiArtworkOptionTypeEnum.CUSTOM_STYLE_PARAM) {
      Modal.confirm({
        title: '提示',
        maskStyle: {
          backgroundColor: 'rgba(0,0,0,0.8)',
        },
        width: 270,
        className: `hookModal dark`,
        content: (
          <div className="text-neutral-400 text-sm text-center">
            确认移除此风格模型吗？
          </div>
        ),
        okText: '确认',
        onOk: async close => {
          try {
            await deleteCustomStyleAction({
              variables: {
                key: item.key!,
              },
            })
            onCollectOption(item.key!, !item.collected, item.type!)
          } catch (err) {
            console.error(err)
            message.destroy()
            message.error(graphQLErrorMessage(err) || '移除风格模型失败')
          }

          close()
        },
        onCancel: close => {
          close()
        },
      })
    } else {
      collectModelAction({
        variables: {
          param: {
            modelKey: item.key,
            viewType: SocialViewType.MODEL,
            affectedType: AffectedType.STYLE_MODEL,
            actionType: item.collected ? ActionType.CANCEL : ActionType.CONFIRM,
          },
        },
      })
        .then(res => {
          if (res.data?.pictureSocialMutation?.status) {
            onCollectOption(item.key!, !item.collected, item.type!)
          }
        })
        .catch(err => {
          message.error(graphQLErrorMessage(err) || '操作失败，请重试')
        })
    }
  }
  const mjModelThemeOptions = useMemo(
    () => options?.mjModelThemeOptions,
    [options]
  )
  useEffect(() => {
    const index =
      allStyleModelOptions?.findIndex(item => item.key === styleModel) ?? -1
    if (index === -1) {
      setStyleModel(allStyleModelOptions?.[0]?.key!)
      setModelCode(allStyleModelOptions?.[0]?.modelCode!)
      setResolutionKey(allStyleModelOptions?.[0]?.resolutionKey!)
    } else {
    }
  }, [styleModel])

  useEffect(() => {
    const _mjIndex =
      mjModelThemeOptions?.findIndex(item => item?.modelCode === modelCode) ??
      -1
    // 判断基础模型配置的mj模型
    const isStyleModelMid = !!mjModelThemeOptions
      ?.map(it => it?.modelCode)
      ?.includes(modelCode)
    setPaintTimesStep(mjModelThemeOptions?.[_mjIndex]?.generateNum ?? 1)
    setPaintTimes(mjModelThemeOptions?.[_mjIndex]?.generateNum ?? 1)
    setIsMJStyleModel(isStyleModelMid)
  }, [modelCode])

  return (
    <>
      <div className="mb-6">
        <div className="flex justify-between items-center border-b-[1px] border-neutral-300 mb-4">
          <OptionsTabs
            options={styleModelTabs.map(item => {
              return {
                label: item,
              }
            })}
            currentIndex={curStyleModelTabIndex}
            setCurrentIndex={setCurStyleModelTabIndex}
            className="flex-1 !mb-0 !border-0"
          />
        </div>

        <div className="flex content-start flex-wrap gap-3">
          {defaultOptions.length === 0 && (
            <div className="bg-[rgba(255,255,255,.04)] rounded-[12px] flex-1">
              <MyEmpty
                description={
                  curStyleModelTabIndex === 0 ? '暂无收藏' : '暂无数据'
                }
                className={style.empty}
              />
            </div>
          )}
          {defaultOptions.map((item, index) => {
            return (
              <StyleModelItem
                key={index}
                checked={item.key === styleModel}
                onClick={() => onItemClick(item)}
                data={item}
                onCollect={() => onCollect(item)}
                imageClassName="h-[134px]"
                style={{
                  width: 'calc((100% - 48px) / 5)',
                }}
              />
            )
          })}
          {curStyleModelOptions.length > DEFAULT_SHOW_COUNT && (
            <div
              className={classNames(
                'rounded-lg text-neutral-800 font-semibold border-2 border-neutral-200 cursor-pointer overflow-hidden'
              )}
              style={{
                background: 'rgba(35, 38, 47, 0.32)',
                width: 'calc((100% - 48px) / 5)',
              }}
              onClick={() => setVisible(true)}
            >
              <div className="h-[162px] flex items-center justify-center">
                <MyIcon
                  className="text-[14px] leading-[16px] font-normal"
                  icon={'more1'}
                />
                <span className="ml-0.5">更多</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <MyModal
        open={visible}
        title="风格模型"
        onCancel={() => setVisible(false)}
        maskStyle={{
          backgroundColor: 'rgba(0,0,0,0.8)',
        }}
        dark
        width={1440}
        footer={null}
        style={{ top: 0, paddingBottom: 0 }}
        isFullScreen
      >
        <OptionsTabs
          options={styleModelTabs.map(item => {
            return {
              label: item,
            }
          })}
          currentIndex={curStyleModelTabIndex}
          setCurrentIndex={setCurStyleModelTabIndex}
        />
        <div
          className="flex content-start flex-wrap gap-3 min-h-[100px] overflow-auto scrollbar"
          style={{
            height: `calc(100vh - 216px)`, // 216px为其他元素的高度+间距
          }}
        >
          {curStyleModelOptions.length === 0 && (
            <MyEmpty
              description={
                curStyleModelTabIndex === 0 ? '暂无收藏' : '暂无数据'
              }
              className={style.empty}
            />
          )}
          {curStyleModelOptions.map((item, index) => {
            return (
              <StyleModelItem
                key={index}
                checked={item.key === styleModel}
                onClick={() => onItemClick(item)}
                data={item}
                onCollect={() => onCollect(item)}
                style={{
                  width: 'calc((100% - 60px) / 6)',
                }}
                imageClassName="h-[217px]"
              />
            )
          })}
        </div>
        <div className="flex justify-between items-center gap-3 pt-4">
          <MyBtn
            block
            type="default"
            className="!bg-neutral-200 !border-neutral-200 !text-neutral-800"
            onClick={() => {
              setStyleModel(options?.recommendedStyleModelOptions?.[0]?.key!)
              setModelCode(
                options?.recommendedStyleModelOptions?.[0]?.modelCode!
              )
              setVisible(false)
            }}
          >
            重置
          </MyBtn>
          <MyBtn block type="primary" onClick={() => setVisible(false)}>
            确定
          </MyBtn>
        </div>
      </MyModal>
    </>
  )
}
